import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

import x_api_key from "@/keyfile";

import { FITCHECK_SERVICE_URL } from ".";

const fitcheckAPI = axios.create({
	baseURL: FITCHECK_SERVICE_URL,
	headers: {
		"x-api-key": x_api_key,
		"Content-Type": "application/json",
	},
});

// Type definitions based on Zod schema
type FitnessStatus = "MEET" | "PARTIAL" | "MISSING";

interface BaselineItem {
	status: FitnessStatus;
	strengths?: string[];
	gaps?: string[];
	recommendations?: string;
}

interface BaselineCheck {
	education: BaselineItem;
	experience: BaselineItem;
	skills: BaselineItem;
	certification: BaselineItem;
}

interface DecisionGuidance {
	ifApplyNow: [string, string, string];
	ifCloseGapsFirst: [string, string, string];
}

export interface FitCheckAssessment {
	roleTitle: string;
	company?: string;
	baselineCheck: BaselineCheck;
	strengths: string[];
	gaps: string[];
	focusAreas: [string, string, string];
	decisionGuidance: DecisionGuidance;
	encouragementNote: string;
}

export interface FitCheckResponse {
	userId: string;
	jobId: string;
	fitCheck: FitCheckAssessment;
	createdAt: string;
	updatedAt: string;
}

export interface RoleExplainerResponse {
	jobId: string;
	roleExplainer: FitCheckAssessment;
	createdAt: string;
	updatedAt: string;
}

interface UserData {
	firstName: string;
	lastName: string;
	email: string;
	userId: string;
}

interface CreateFitCheckPayload {
	userId: string;
	jobId: string;
	resumeURL: string;
	user: UserData;
	jobDetails: Record<string, unknown>;
}

interface CreateRoleExplainerPayload {
	jobDetails: Record<string, unknown>;
	jobId: string;
	userId: string;
}

export function useFitCheck(userId: string, jobId: string) {
	return useQuery<FitCheckResponse>({
		queryKey: ["fitcheck", userId, jobId],
		queryFn: async () => {
			const { data } = await fitcheckAPI.get<FitCheckResponse>(`/fit-check?userId=${userId}&jobId=${jobId}`);
			return data;
		},
		enabled: !!userId && !!jobId,
		retryDelay: 100,
	});
}

// mutation to create a fitcheck request
export function useCreateFitCheckMutation() {
	return useMutation<FitCheckResponse, Error, CreateFitCheckPayload>({
		mutationFn: async ({ userId, jobId, resumeURL, user, jobDetails }: CreateFitCheckPayload) => {
			const { data } = await fitcheckAPI.post<FitCheckResponse>("/fit-check", {
				userId,
				jobId,
				resumeURL,
				user,
				jobDetails,
			});
			return data;
		},
	});
}

interface FitChecksListResponse {
	success: boolean;
	userId: string;
	count: number;
	fitChecks: FitCheckResponse[];
}

// get fitcheck history for a user "/fit-checks/:userId"
export function useFitChecks(userId: string) {
	return useQuery<FitCheckResponse[]>({
		queryKey: ["fit-checks", userId],
		queryFn: async () => {
			const { data } = await fitcheckAPI.get<FitChecksListResponse>(`/fit-checks/${userId}`);
			return data.fitChecks;
		},
		enabled: !!userId,
	});
}

// mutation to create a role explainer request
export function useCreateRoleExplanationMutation() {
	return useMutation<RoleExplainerResponse, Error, CreateRoleExplainerPayload>({
		mutationFn: async ({ jobDetails, jobId, userId }: CreateRoleExplainerPayload) => {
			const { data } = await fitcheckAPI.post<RoleExplainerResponse>("/role-explainer", {
				jobDetails,
				jobId,
				userId,
			});
			return data;
		},
	});
}
